import React, { useState, useEffect } from "react";
import { fetchAirlinesApi } from "apis/authServices";
import { updateJackpotConfiguration } from "apis/adminServices/jackpotServices";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { JackpotConfigUpdate } from "apis/adminServices/jackpotServices";
import { useToastStore } from "store/toastStore";
import { AirlinePicker } from "components/admin";

interface Airline {
  id: string;
  airlineName: string;
}

interface JackpotConfigUpdateExtended extends JackpotConfigUpdate {
  configurations?: { jackpotId: number; airlineId: number }[];
}

interface EditJackpotModalProps {
  isOpen: boolean;
  jackpot: JackpotConfigUpdateExtended;
  onClose: () => void;
  onUpdate?: (updatedJackpot: JackpotConfigUpdate) => void;
  refreshTable?: () => void; //  prop to refresh table data
}

const EditJackpotModal: React.FC<EditJackpotModalProps> = ({ isOpen, jackpot, onClose, onUpdate, refreshTable }) => {
  const [formData, setFormData] = useState<JackpotConfigUpdate & { selectedAirlineIds: number[] }>({
    jackpotId: jackpot.jackpotId,
    airlineId: jackpot.configurations?.map((config) => config.airlineId) || [],
    configurationName: jackpot.configurationName,
    answer: jackpot.answer,
    validFrom: jackpot.validFrom,
    validTo: jackpot.validTo,
    isActive: jackpot.isActive,
    selectedAirlineIds: jackpot.configurations?.map((config) => config.airlineId) || [],
  });
  const [initialAirlineNames, setInitialAirlineNames] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { showToast } = useToastStore();
  const [creditError, setCreditError] = useState<string>("");

  // Fetch airline names when modal opens to pass as initialAirlineNames
  useEffect(() => {
    if (isOpen) {
      const fetchInitialAirlines = async () => {
        try {
          const initialAirlineIds = jackpot.configurations?.map((config) => config.airlineId) || [];
          if (initialAirlineIds.length > 0) {
            const response = await fetchAirlinesApi("");
            const fetchedAirlines = Array.isArray(response) ? response : [];
            const names = fetchedAirlines
              .filter((airline) => initialAirlineIds.includes(parseInt(airline.id, 10)))
              .map((airline) => airline.airlineName);
            setInitialAirlineNames(names);
          }
        } catch (error) {
          console.error("Error fetching initial airlines:", error);
        }
      };
      fetchInitialAirlines();
    }
  }, [isOpen, jackpot.configurations]);

  const handleAirlineChange = (airlineIds: number[]) => {
    setFormData({
      ...formData,
      selectedAirlineIds: airlineIds,
      airlineId: airlineIds,
    });
  };

  const handleDateChange = (field: "validFrom" | "validTo") => (date: Date | null) => {
    if (date) {
      const isoDate = `${date.toISOString().split("T")[0]}T00:00:00.000Z`;
      if (field === "validTo" && formData.validFrom && new Date(isoDate) < new Date(formData.validFrom)) {
        setError("Valid To must be after Valid From");
        return;
      }
      setFormData({ ...formData, [field]: isoDate });
      setError(null);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    if (creditError) {
      setError("Please fix validation errors before submitting");
      setLoading(false);
      return;
    }

    if (formData.answer > 100) {
      setError("Credit value cannot exceed 100");
      setLoading(false);
      return;
    }
    const originalData = {
      jackpotId: jackpot.jackpotId,
      airlineId: jackpot.configurations?.map((c) => c.airlineId) || [],
      configurationName: jackpot.configurationName,
      answer: jackpot.answer,
      validFrom: jackpot.validFrom,
      validTo: jackpot.validTo,
      isActive: jackpot.isActive,
    };

    // STEP 2: Build updated data (same shape)
    const updatedData = {
      jackpotId: formData.jackpotId,
      airlineId: formData.selectedAirlineIds || [],
      configurationName: formData.configurationName,
      answer: formData.answer,
      validFrom: formData.validFrom,
      validTo: formData.validTo,
      isActive: formData.isActive,
    };

    // STEP 3: Compare both objects
    const hasChanges = JSON.stringify(originalData) !== JSON.stringify(updatedData);
    if (!hasChanges) {
      setLoading(false);
      showToast("No changes", "info");
      // onClose();
      return;
    }
    const apiConfig: JackpotConfigUpdate = {
      jackpotId: formData.jackpotId,
      airlineId: formData.selectedAirlineIds || [],
      configurationName: formData.configurationName,
      answer: formData.answer,
      validFrom: formData.validFrom,
      validTo: formData.validTo,
      isActive: formData.isActive,
    };

    try {
      const response = await updateJackpotConfiguration(apiConfig);
      if (response.success) {
        onUpdate?.(apiConfig);
        refreshTable?.(); // Refresh table data after successful save
        onClose();
        showToast("Jackpot configuration updated successfully", "success");
      } else {
        setError(response.error || "Failed to update jackpot configuration");
      }
    } catch (err) {
      setError("An unexpected error occurred");
      console.error("Error updating jackpot configuration:", err);
    } finally {
      setLoading(false);
    }
  };


  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50" onClick={onClose}>
      <div className="relative p-4 w-full max-w-xl" onClick={(e) => e.stopPropagation()}>
        <div className="relative bg-white rounded-lg shadow pb-4">
          <div className="flex justify-between mx-4 py-4 rounded-t border-b border-lightgray-100">
            <p className="text-base font-semibold text-grease-100">Edit Jackpot Configuration</p>
            <button
              type="button"
              className="text-gray-400 bg-transparent rounded-lg text-sm w-8 h-8 inline-flex justify-center items-center"
              onClick={onClose}
              disabled={loading}
            >
              <svg className="w-3 h-3" viewBox="0 0 14 14" fill="none">
                <path
                  stroke="currentColor"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="m1 1 6 6m0 0 6 6M7 7l6-6M7 7l-6 6"
                />
              </svg>
              <span className="sr-only">Close</span>
            </button>
          </div>

          <form onSubmit={handleSubmit}>
            <div className="px-4 py-4">
              <div className="mb-4">
                <label htmlFor="config-name" className="text-xs font-light text-gray-500">
                  Configuration Name
                  <span className="text-red-500 ml-1">*</span>
                </label>
                <input
                  type="text"
                  id="config-name"
                  value={formData.configurationName}
                  className="w-full p-3 rounded-lg bg-gray-100 text-xs text-grease-100 disabled:bg-gray-200 border-none"
                  disabled
                />
              </div>

              <div className="mb-4">
                <AirlinePicker
                  selectedAirlineIds={formData.selectedAirlineIds || []}
                  onChange={handleAirlineChange}
                  placeholder="Search airlines..."
                  label="Flights"
                  showChips={true}
                  disabled={loading}
                  isRequired={true}
                  initialAirlineNames={initialAirlineNames}
                  showConfirmButton={true}
                />
              </div>

              <div className="mb-4">
                <label className="block text-xs text-gray-500 mb-2" htmlFor="answer">
                  Tokens for Answering a Request
                </label>
                <input
                  type="number"
                  id="answer"
                  value={formData.answer}
                  onChange={(e) => {
                    const value = parseInt(e.target.value, 10) || 0;
                    if (value > 100) {
                      setCreditError("Credit value cannot exceed 100");
                      return;
                    }
                    setCreditError("");
                    setFormData({
                      ...formData,
                      answer: value,
                    });
                  }}
                  className="w-full p-3 rounded-lg bg-lightblue-100 text-xs text-grease-100 focus:outline-none disabled:bg-gray-200 border-none"
                  min="0"
                  max="100"
                  disabled={loading}
                />
                {creditError && <p className="text-red-500 text-xs mt-1">{creditError}</p>}
              </div>

              <div className="mb-4 flex gap-4">
                <div className="flex-1">
                  <label className="block text-xs text-gray-500 mb-2" htmlFor="validFrom">
                    Valid From
                    <span className="text-red-500 ml-1">*</span>
                  </label>
                  <DatePicker
                    id="validFrom"
                    selected={formData.validFrom ? new Date(formData.validFrom) : null}
                    onChange={handleDateChange("validFrom")}
                    dateFormat="yyyy-MM-dd"
                    minDate={new Date(new Date().setHours(0, 0, 0, 0))}
                    className="w-full p-3 rounded-lg bg-lightblue-100 text-xs text-grease-100 focus:outline-none disabled:bg-gray-200 border-none"
                    placeholderText="Select start date"
                    disabled={loading}
                    onFocus={(e) => e.target.setAttribute("autocomplete", "off")}
                  />
                </div>
                <div className="flex-1">
                  <label className="block text-xs text-gray-500 mb-2" htmlFor="validTo">
                    Valid To
                    <span className="text-red-500 ml-1">*</span>
                  </label>
                  <DatePicker
                    id="validTo"
                    selected={formData.validTo ? new Date(formData.validTo) : null}
                    onChange={handleDateChange("validTo")}
                    dateFormat="yyyy-MM-dd"
                    minDate={formData.validFrom ? new Date(formData.validFrom) : new Date(new Date().setHours(0, 0, 0, 0))}
                    className="w-full p-3 rounded-lg bg-lightblue-100 text-xs text-grease-100 focus:outline-none disabled:bg-gray-200 border-none"
                    placeholderText="Select end date"
                    disabled={loading}
                    onFocus={(e) => e.target.setAttribute("autocomplete", "off")}
                  />
                </div>
              </div>

              <div className="mb-4">
                <label className="block text-xs text-gray-500 mb-2">Status</label>
                <select
                  value={formData.isActive ? "true" : "false"}
                  onChange={(e) => setFormData({ ...formData, isActive: e.target.value === "true" })}
                  className="w-full p-3 rounded-lg bg-lightblue-100 text-xs text-grease-100 focus:outline-none disabled:bg-gray-200 border-none"
                  disabled={loading}
                >
                  <option value="true">Active</option>
                  <option value="false">Disabled</option>
                </select>
              </div>

              {error && <div className="mb-4 text-red-500 text-xs">{error}</div>}

              <div className="flex justify-end gap-3">
                <button type="button" onClick={onClose} className="px-4 py-2 text-xs text-gray-600 disabled:opacity-50" disabled={loading}>
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-[#FFD700] text-white text-xs rounded-lg disabled:bg-[#FFD700] disabled:opacity-70 disabled:cursor-not-allowed"
                  disabled={loading}
                >
                  {loading ? "Saving..." : "Save"}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default EditJackpotModal;
